import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, ArrowRightIcon, ArrowUturnLeftIcon, UserMinusIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { openFile } from "../../utils/openFile";
import { Button, Container, EmptyState, Loading, PageHeader, RowAction, Select, Table, Tag } from "../ui";
import { abroadEvaluatorApi } from "../../api/abroadLoans";
import { errorMessageOf, problemOf } from "../../api/scholarships";
import { EVALUATION_STATES, evaluationStateOf, formatDateTime } from "../scholarships/review/model";
import EligibilityFlag, { ApplicantName } from "../scholarships/review/EligibilityFlag";
import { EVALUATOR_KEY } from "../scholarships/evaluator/EvaluatorPages";
import { ConsentCard, RemoveApplicantModal, restoreApplicant } from "../scholarships/evaluator/Consent";
import { MarksCard, hasInvalidMarks, marksBodyOf, marksOf, unmarkedOf } from "../scholarships/evaluator/Marks";
import { ApplicationDetails } from "./ApplicationSummary";
import { programmeLabel } from "./model";
import { ELIGIBILITY_LABELS } from "./student";

// Under EVALUATOR_KEY, so the shared consent and conflict helpers refresh these too; a function, as the two modules import each other
const abroadEvaluatorKey = (...rest) => [...EVALUATOR_KEY, "abroad", ...rest];

export const abroadQueuePath = (callId) => `/evaluation/loans/${callId}`;
const abroadMarkingPath = (applicationId) => `/evaluation/loans/applications/${applicationId}`;

export const CALL_KINDS = { LOAN: "Loan abroad", BURSARY: "Bursary abroad" };

// Where the evaluator stands on one applicant, in the scholarships' terms
const markingStateOf = (row) => (row.removedForConflict ? "CONFLICTED" : row.myEvaluation ?? "NOT_STARTED");

const SUBMIT_NOTE = "Your marks and comments are then kept permanently: you cannot change them unless HEC reopens them.";

// The published abroad calls this evaluator sits on the committee of
export const useAbroadMarkingCalls = () =>
  useQuery({
    queryKey: abroadEvaluatorKey("calls"),
    queryFn: async () => (await abroadEvaluatorApi.calls()).data,
  });

// One abroad call's applicants for this evaluator, once they have consented to the committee's rules
export function AbroadEvaluatorApplicationsPage() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const back = useLocation().state?.from ?? "/evaluation";
  const [markingState, setMarkingState] = useState("");
  const [removing, setRemoving] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const { data: calls } = useAbroadMarkingCalls();
  const call = calls?.find((c) => c.id === callId);
  const { data = [], isPending, isError, error } = useQuery({
    queryKey: abroadEvaluatorKey("applications", callId),
    retry: (count, err) => problemOf(err)?.code !== "consent-required" && count < 2,
    queryFn: async () => (await abroadEvaluatorApi.queue(callId)).data,
  });
  const needsConsent = isError && problemOf(error)?.code === "consent-required";
  const rows = markingState ? data.filter((r) => markingStateOf(r) === markingState) : data;

  const putBack = async (r) => {
    setRestoring(r.applicationId);
    await restoreApplicant(queryClient, r.applicationId, abroadEvaluatorApi);
    setRestoring(null);
  };

  const columns = [
    { key: "applicant", label: "Applicant", accessor: (r) => r.applicantName, render: (r) => <ApplicantName name={r.applicantName} meets={r.meetsEligibility} /> },
    { key: "programme", label: "Programme", accessor: (r) => r.programmeName ?? "—" },
    { key: "submittedAt", label: "Submitted", accessor: (r) => formatDateTime(r.submittedAt), sortValue: (r) => r.submittedAt },
    {
      key: "state",
      label: "My marking",
      accessor: (r) => evaluationStateOf(markingStateOf(r)).label,
      render: (r) => <Tag color={evaluationStateOf(markingStateOf(r)).color}>{evaluationStateOf(markingStateOf(r)).label}</Tag>,
    },
    {
      key: "open",
      label: "",
      exportable: false,
      render: (r) =>
        r.removedForConflict ? (
          <RowAction Icon={ArrowUturnLeftIcon} label="Put back" onClick={() => putBack(r)} loading={restoring === r.applicationId} />
        ) : (
          <span className="flex justify-end gap-1">
            {r.myEvaluation !== "SUBMITTED" && <RowAction Icon={UserMinusIcon} label="Remove (conflict)" variant="danger-soft" onClick={() => setRemoving(r)} />}
            <RowAction
              Icon={ArrowRightIcon}
              label={r.myEvaluation === "SUBMITTED" ? "View" : "Mark"}
              onClick={() => navigate(abroadMarkingPath(r.applicationId), { state: { applicantName: r.applicantName } })}
            />
          </span>
        ),
    },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={back} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> My calls
      </Link>
      <PageHeader title="Applications to review" subtitle={call && `${call.name} · ${CALL_KINDS[call.kind] ?? call.kind}`} />
      {needsConsent ? (
        <ConsentCard scholarshipId={callId} api={abroadEvaluatorApi} committee="Loan Review Committee" />
      ) : (
        <Table cardView viewKey="abroad-evaluator-calls"
          columns={columns}
          data={rows}
          isLoading={isPending}
          searchable={false}
          exportable={false}
          emptyTitle={isError ? "Could not load the applications." : "No applications"}
          filters={
            <Select aria-label="My marking" placeholder="All" value={markingState} onChange={(e) => setMarkingState(e.target.value)} options={EVALUATION_STATES} className="w-full sm:w-56" />
          }
        />
      )}
      <RemoveApplicantModal
        applicant={removing?.applicantName}
        applicationId={removing?.applicationId}
        api={abroadEvaluatorApi}
        onClose={() => setRemoving(null)}
        onRemoved={() => setRemoving(null)}
      />
    </Container>
  );
}

/**
 * An evaluator marks one abroad application against its programme's criteria. Marks are saved as a
 * draft until submitted; a submitted evaluation is final unless the HEC Admin reopens it.
 */
export function AbroadMarkingPage() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const applicantName = useLocation().state?.applicantName;
  const key = abroadEvaluatorKey("application", applicationId);
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: key,
    retry: (count, err) => problemOf(err)?.code !== "consent-required" && problemOf(err)?.status !== 404 && count < 2,
    queryFn: async () => (await abroadEvaluatorApi.application(applicationId)).data,
  });
  const [marks, setMarks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState([]);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (data) setMarks(marksOf(data.myEvaluation));
  }, [data]);

  const problem = isError && problemOf(error);
  if (isPending) return <Loading label="Loading the application…" />;
  if (problem?.code === "consent-required")
    return (
      <Container className="flex flex-col gap-6 py-6">
        <PageHeader title="Before you start" />
        <EmptyState title="Consent to the committee's rules first" description="Open the call from your list to give your consent, then come back to this applicant." />
      </Container>
    );
  if (isError || !marks) return <EmptyState title="This application is not available to you" description="It may not be assigned to you, or you removed this applicant from your list." onRefresh={refetch} />;

  const { application, criteria, myEvaluation: evaluation } = data;
  const submitted = evaluation?.state === "SUBMITTED";
  const changed = JSON.stringify(marks) !== JSON.stringify(marksOf(evaluation));
  const queuePath = abroadQueuePath(application.callId);
  const unmet = application.unmetEligibility ?? [];

  const setMark = (criterionId, patch) => {
    setMissing((prev) => prev.filter((id) => id !== criterionId));
    setMarks((prev) => ({ ...prev, [criterionId]: { marks: "", comment: "", ...prev[criterionId], ...patch } }));
  };

  // The evaluation is a separate resource; keep the application's copy in step with it
  const keep = (myEvaluation) => queryClient.setQueryData(key, { ...data, myEvaluation });

  const saveDraft = async ({ quiet = false } = {}) => {
    if (hasInvalidMarks(criteria, marks)) {
      toast.error("Marks must be between 0 and 100");
      return false;
    }
    setBusy(true);
    try {
      keep((await abroadEvaluatorApi.saveMarks(applicationId, marksBodyOf(criteria, marks))).data);
      if (!quiet) toast.success("Marks saved as a draft");
      return true;
    } catch (err) {
      console.error(`Failed to save marks for abroad application ${applicationId}:`, err);
      toast.error(errorMessageOf(err, "Failed to save your marks. Please try again."));
      if (problemOf(err)?.code === "changed-meanwhile") refetch();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (changed && !(await saveDraft({ quiet: true }))) return;
    setBusy(true);
    try {
      keep((await abroadEvaluatorApi.submitMarks(applicationId)).data);
      queryClient.invalidateQueries({ queryKey: abroadEvaluatorKey("applications") });
      queryClient.invalidateQueries({ queryKey: abroadEvaluatorKey("calls") });
      toast.success("Marks submitted");
    } catch (err) {
      console.error(`Failed to submit marks for abroad application ${applicationId}:`, err);
      setMissing(unmarkedOf(problemOf(err)));
      toast.error(errorMessageOf(err, "Failed to submit your marks. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const open = (load, fileName) => openFile(async () => (await load()).data, fileName);
  const state = evaluationStateOf(evaluation?.state ?? "NOT_STARTED");

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={queuePath} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> Applications to review
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {applicantName ?? "Application"}
            <Tag color={state.color}>{state.label}</Tag>
          </span>
        }
        subtitle={`${application.callName} · ${programmeLabel(application.programme)} · submitted ${formatDateTime(application.submittedAt)}`}
        actions={
          !submitted && (
            <Button variant="danger-soft" icon={<UserMinusIcon className="h-4 w-4" />} onClick={() => setRemoving(true)}>
              I have a conflict
            </Button>
          )
        }
      />

      <EligibilityFlag eligibility={unmet.length ? { eligible: false, unmetRules: unmet.map((kind) => ({ kind, label: ELIGIBILITY_LABELS[kind] ?? kind })) } : null} />

      <ApplicationDetails
        application={application}
        onOpen={(d) => open(() => abroadEvaluatorApi.document(applicationId, d.id), d.fileName)}
        onOpenLegacy={(d) => open(() => abroadEvaluatorApi.legacyDocument(applicationId, d.id), d.fileName)}
      />

      <MarksCard
        rubric={criteria}
        marks={marks}
        evaluation={evaluation}
        changed={changed}
        missing={missing}
        busy={busy}
        onChange={setMark}
        onSave={() => saveDraft()}
        onSubmit={submit}
        submitNote={SUBMIT_NOTE}
      />

      <RemoveApplicantModal
        applicant={applicantName}
        applicationId={removing ? applicationId : null}
        api={abroadEvaluatorApi}
        onClose={() => setRemoving(false)}
        onRemoved={() => navigate(queuePath)}
      />
    </Container>
  );
}
